import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { LayoutTemplate, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { CardContent, CardIcon } from '@/components/ui/card'
import { useNetwork } from '@/contexts/NetworkContext'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { apiErrorDescription } from '@/services/core/apiError'
import { newReportId } from '@/services/reports/reportsApi'
import type { ListSort } from '@/services/core/paging'
import {
  ReportTemplatesApi,
  type ReportTemplateDetail,
} from '@/services/reports/reportTemplatesApi'
import { AppShell } from '../../components/AppShell'
import { BulkActionBar } from '../../components/BulkActionBar'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'
import { SearchInput } from '../../components/SearchInput'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ReportTemplateTable } from '../../components/reports/ReportTemplateTable'

export function ReportTemplatesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { scopeKey, scopeReady } = useNetwork()
  const [search, setSearch] = useUrlSearch()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>(
    'filters.reportTemplates.status',
    'ACTIVE'
  )
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const [selection, setSelection] = useState<ReportTemplateDetail[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['report-templates', 'page', scopeKey, search, statusFilter, page, pageSize, sort],
    queryFn: () =>
      ReportTemplatesApi.getPage({ page, pageSize, search, status: statusFilter, sort }),
    enabled: scopeReady,
    staleTime: 5 * 60 * 1000,
  })
  const templates = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(
        selection.map((tpl) => ReportTemplatesApi.update(tpl.id, { name: tpl.name, objectState: state }))
      )
      queryClient.invalidateQueries({ queryKey: ['report-templates'] })
      toast.success(t('common.done'), {
        description: t(
          state === 'ACTIVE'
            ? 'reportTemplates.doneDetail_activate'
            : 'reportTemplates.doneDetail_deactivate',
          { count: selection.length }
        ),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  return (
    <AppShell crumbs={[{ label: t('reportTemplates.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('reportTemplates.title')}</h1>
            <p>{t('reportTemplates.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button onClick={() => navigate(`/reports/templates/${newReportId()}?new=true`)}>
              <Plus className="size-4" /> {t('reportTemplates.newTemplate')}
            </Button>
          </div>
        </div>

        <ListCard tone="insight">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={LayoutTemplate} />
                {t('reportTemplates.title')}
              </>
            }
            count={totalCount}
            actions={
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => refilter(() => setPageSize(size))}
              />
            }
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => refilter(() => setStatusFilter(v))}
                />
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              {
                label: t('common.activate'),
                icon: 'pi pi-check-circle',
                severity: 'success',
                onClick: () => bulkSetStatus('ACTIVE'),
              },
              {
                label: t('common.deactivate'),
                icon: 'pi pi-ban',
                severity: 'danger',
                onClick: () => bulkSetStatus('INACTIVE'),
              },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <ReportTemplateTable
              templates={templates}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('reportTemplates.templateCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
